import React from 'react';
import { InputLabel, Select, MenuItem, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useLocales } from '../../../../hooks';
import { SetterOrUpdater } from 'recoil';
import FormControl from '../../../shared/FormControl';
import { treatmentPickerTestIds } from '../../../shared/TestsIds';

export interface ITreatmentPickerProps<T extends Record<string, string>> {
  options: T;
  value: keyof T;
  onChange: SetterOrUpdater<keyof T>;
  disabled?: boolean;
  helpMessage?: Partial<Record<keyof T, string>>;
  disabledOptions?: string[];
}

const useStyles = makeStyles()(() => ({
  selector: {
    minWidth: 260
  }
}));

export function TreatmentPicker<T extends Record<string, string>>({
  options,
  value,
  onChange,
  disabled = false,
  helpMessage,
  disabledOptions
}: ITreatmentPickerProps<T>): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();

  return (
    <FormControl>
      <InputLabel id="treatmentSelector">{t('layouts.treatment')}</InputLabel>
      <Select
        label={t('layouts.treatment')}
        className={classes.selector}
        labelId="treatmentSelector"
        value={value}
        onChange={({ target: { value } }) => onChange(value)}
        data-testid={treatmentPickerTestIds.treatmentSelector}
        disabled={disabled}
      >
        {Object.values(options).map((value) => {
          const menuItemContent = <span>{value}</span>;
          return (
            <MenuItem
              key={value}
              value={value}
              disabled={disabledOptions?.includes(value)}
              data-testid={treatmentPickerTestIds.treatmentSelectorItem}
              data-treatment-id={value}
            >
              {helpMessage && helpMessage[value] ? (
                <Tooltip title={helpMessage[value]} key={`${value}-tooltip`} placement="right">
                  {menuItemContent}
                </Tooltip>
              ) : (
                menuItemContent
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
